import { ChangeEvent, Component } from "react";
import {
    createStyles,
    Paper,
    Theme,
    WithStyles,
    withStyles
} from "@material-ui/core";
import $ from "jquery";
import * as postcode from "postcode-validator";
import ReactGA from "react-ga4";
import { connect } from "react-redux";
import { Redirect } from "react-router";
import { bindActionCreators } from "redux";
import { RedirectTo } from "../../helpers/RedirectTo";
import { trackWithStackAdaptPixel } from "../../hooks/useStackAdaptPixel";
import IRegisterAccount from "../../models/account/IRegisterAccount";
import Constants from "../../models/utility/Constants";
import { LoadState } from "../../models/utility/LoadState";
import * as AppConfigStore from "../../store/AppConfigStore";
import FormErrorWrapper from "../shared/FormErrorWrapper";
import { AppInsightsLogger } from "../shared/logging/AppInsightsLogger";
import AccountDetails from "../shared/registration/AccountDetails";
import CreditCardInput from "../shared/registration/CreditCardInput";
import ProfileInformation from "../shared/registration/ProfileInformation";
import SectionBanner from "../shared/SectionBanner";
import { IAppDispatch, IAppState } from "./../../store";
import * as UserStore from "./../../store/UserStore";

type RegisterProps = UserStore.IUserStoreState &
    typeof UserStore.actionCreators &
    AppConfigStore.IAppConfigStoreState &
    WithStyles<typeof styles>;

interface IRegisterState {
    firstName: string;
    lastName: string;
    email: string;
    confirmEmail: string;
    password: string;
    confirmPassword: string;
    phoneNumber: string;
    address1: string;
    address2: string;
    country: string;
    province: string;
    city: string;
    postalCode: string;
    acceptTerms: boolean;
    captcha: string | null;
    hasErrors: boolean;
    errorMessages: string[];
}

const styles = (theme: Theme) =>
    createStyles({
        form: {
            maxWidth: 1000,
            padding: theme.spacing(8),
            width: "100%"
        },
        paper: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#231F20"
        }
    });

class Register extends Component<RegisterProps, IRegisterState> {
    constructor(props: RegisterProps) {
        super(props);

        Constants.SetDocumentTitle("Register");

        this.props.toggleRegFailure(false, []);
        this.state = {
            acceptTerms: false,
            address1: "",
            address2: "",
            captcha: null,
            city: "",
            confirmPassword: "",
            country: "CA",
            email: "",
            confirmEmail: "",
            errorMessages: [],
            firstName: "",
            hasErrors: false,
            lastName: "",
            password: "",
            phoneNumber: "",
            postalCode: "",
            province: ""
        };

        this.props.loadRegistrationPublicTokens();
    }

    public componentDidUpdate(
        prevProps: RegisterProps,
        prevState: IRegisterState
    ) {
        if (
            (this.props.regFailErrors &&
                this.props.regFailErrors.length > 0 &&
                prevProps.regFailErrors !== this.props.regFailErrors) ||
            (this.state.errorMessages &&
                this.state.errorMessages.length > 0 &&
                prevState.errorMessages !== this.state.errorMessages)
        ) {
            // scroll to top if there are new error messages
            $("body, html").animate(
                { scrollTop: 0 },
                Constants.ScrollAnimationLength
            );
        }
    }

    public render() {
        if (this.props.user) {
            if (this.props.prevLocation) {
                return <Redirect to={this.props.prevLocation} />;
            } else {
                RedirectTo(
                    Constants.Routes.AuctionList({
                        baseUrl: this.props.salesPageUrl
                    })
                );
            }
        }

        const classes = this.props.classes;
        const hasErrors = this.state.hasErrors || this.props.regFailure;
        const errorMessages = this.state.errorMessages.concat(
            this.props.regFailErrors
        );
        return (
            <Paper className={classes.paper} elevation={2}>
                <SectionBanner leftSideText="Register" />
                <div className={classes.form}>
                    <form>
                        {hasErrors ? (
                            <FormErrorWrapper
                                errors={errorMessages}
                                errorMessage="The following errors occurred during registration:"
                            />
                        ) : null}
                        <AccountDetails
                            firstName={this.state.firstName}
                            lastName={this.state.lastName}
                            email={this.state.email}
                            isEdit={false}
                            isViewMode={false}
                            showPersonalCapacityNotice={true}
                            allowPasswordReset={true}
                            onChange={this.onChange}
                        />
                        <ProfileInformation
                            phoneNumber={this.state.phoneNumber}
                            address1={this.state.address1}
                            address2={this.state.address2}
                            country={this.state.country}
                            province={this.state.province}
                            city={this.state.city}
                            postalCode={this.state.postalCode}
                            isViewMode={false}
                            onChange={this.onChange}
                        />
                        <CreditCardInput
                            captchaKey={this.props.captchaKey}
                            onRegister={this.onSubmit}
                            onCreditCardError={this.onCreditCardError}
                            handleTermsChange={this.handleTermsChange}
                            handleCaptchaReceived={this.handleCaptchaReceived}
                            registerDisabled={
                                this.props.authLoadState === LoadState.Loading
                            }
                            firstName={this.state.firstName}
                            lastName={this.state.lastName}
                            address1={this.state.address1}
                            address2={this.state.address2}
                            city={this.state.city}
                            province={this.state.province}
                            postalCode={this.state.postalCode}
                            country={this.state.country}
                        />
                    </form>
                </div>
            </Paper>
        );
    }

    private onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value as string;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.setState({ [name]: value } as any);

        // Reset province on country change
        if (name === "country") {
            this.setState({ province: "" });
        }
    };

    private handleCaptchaReceived = (captcha: string | null) => {
        this.setState({ captcha });
    };

    private handleTermsChange = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({ acceptTerms: event.target.checked });
    };

    private onCreditCardError = (errorMessage: string) => {
        this.validateForm(errorMessage);
    };

    private isASCII = (str: string) => {
        // eslint-disable-next-line no-control-regex
        return /^[\x00-\x7F]*$/.test(str);
    };

    private validateForm = (creditCardError: string) => {
        const errorMessages: string[] = [];
        const requiredMessage = " field is required.";
        if (this.state.firstName.trim().length === 0) {
            errorMessages.push("First Name" + requiredMessage);
        } else if (!this.isASCII(this.state.firstName.trim())) {
            errorMessages.push("First Name contains invalid character(s).");
        }
        if (this.state.lastName.trim().length === 0) {
            errorMessages.push("Last Name" + requiredMessage);
        } else if (!this.isASCII(this.state.lastName.trim())) {
            errorMessages.push("Last Name contains invalid character(s).");
        }
        if (this.state.email.trim().length === 0) {
            errorMessages.push("Email" + requiredMessage);
        } else if (!this.state.email.trim().match(Constants.EmailRegExp)) {
            errorMessages.push("Invalid Email Address");
        } else if (this.state.email !== this.state.confirmEmail) {
            errorMessages.push("Provided emails do not match.");
        } else if (!this.isASCII(this.state.email.trim())) {
            errorMessages.push("Email Address contains invalid character(s).");
        }
        if (this.state.password.trim().length === 0) {
            errorMessages.push("Password" + requiredMessage);
        } else if (this.state.confirmPassword.trim().length === 0) {
            errorMessages.push("Confirm Password" + requiredMessage);
        } else if (this.state.password !== this.state.confirmPassword) {
            errorMessages.push("Provided passwords do not match.");
        } else if (!this.isASCII(this.state.password.trim())) {
            errorMessages.push("Password contains invalid character(s).");
        }
        if (this.state.phoneNumber.trim().length === 0) {
            errorMessages.push("Phone Number" + requiredMessage);
        }
        if (this.state.address1.trim().length === 0) {
            errorMessages.push("Address" + requiredMessage);
        } else if (
            !this.isASCII(this.state.address1.trim()) ||
            !this.isASCII(this.state.address2.trim())
        ) {
            errorMessages.push("Address contains invalid character(s).");
        }
        if (this.state.country.trim().length === 0) {
            errorMessages.push("Country" + requiredMessage);
        }
        if (this.state.province.trim().length === 0) {
            errorMessages.push("Province/State" + requiredMessage);
        }
        if (this.state.city.trim().length === 0) {
            errorMessages.push("Town/City" + requiredMessage);
        } else if (!this.isASCII(this.state.city.trim())) {
            errorMessages.push("Town/City contains invalid character(s).");
        }
        if (this.state.postalCode.trim().length === 0) {
            errorMessages.push("Postal Code/Zip" + requiredMessage);
        } else if (this.state.country === "CA" || this.state.country === "US") {
            if (
                !postcode.postcodeValidator(
                    this.state.postalCode,
                    this.state.country
                )
            ) {
                errorMessages.push("Postal Code/Zip is not valid.");
            }
        }
        if (creditCardError !== "") {
            errorMessages.push(creditCardError);
        }
        if (!this.state.acceptTerms) {
            errorMessages.push("Terms and Conditions must be accepted.");
        }
        if (!this.state.captcha || this.state.captcha === "") {
            errorMessages.push("Failed to validate Google reCAPTCHA.");
        }
        const hasErrors = errorMessages.length > 0;
        this.setState({ hasErrors, errorMessages });

        if (hasErrors) {
            ReactGA.event(Constants.GAEvents.Form.Invalid);
            AppInsightsLogger.trackException({
                exception: new Error("Registration Validation Error"),
                properties: { errors: errorMessages }
            });
        }

        return !hasErrors;
    };

    private onSubmit = (paymentMethodId: string) => {
        this.setState({ hasErrors: false, errorMessages: [] });

        if (this.validateForm("")) {
            ReactGA.event(Constants.GAEvents.Form.Submit);
            ReactGA.event(Constants.GAEvents.SignUp);
            AppInsightsLogger.trackEvent({ name: "Registration Succeeded" });
            trackWithStackAdaptPixel(
                this.props.stackAdaptPixelId,
                Constants.StackAdaptEvents.CompleteRegistration,
                {
                    userEmail: this.state.email
                }
            );

            const model = {
                AcceptTerms: this.state.acceptTerms,
                Address1: this.state.address1.trim(),
                Address2: this.state.address2.trim(),
                City: this.state.city.trim(),
                ConfirmPassword: this.state.confirmPassword,
                Country: this.state.country.trim(),
                PaymentMethodId: paymentMethodId,
                Email: this.state.email.trim(),
                FirstName: this.state.firstName.trim(),
                LastName: this.state.lastName.trim(),
                Password: this.state.password,
                PhoneNumber: this.state.phoneNumber.trim(),
                PostalCode: this.state.postalCode.trim(),
                Province: this.state.province.trim()
            } as IRegisterAccount;

            this.props.registerUser(model);
        }
    };
}

const reduxState = (state: IAppState) => ({
    ...state.userStoreState,
    ...state.appConfigStoreState
});
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(UserStore.actionCreators, dispatch);

export default withStyles(styles)(connect(reduxState, reduxDispatch)(Register));
